/**
 * ================================================================================================
 *  THE APPROVED NAVIGATION, AND WHAT EACH DESTINATION ACTUALLY HAS BEHIND IT.
 * ================================================================================================
 *
 * The approved prototypes name nine platform destinations and eight customer ones. The product
 * serves some of them today and not others, and the Owner was explicit about how that difference
 * is handled:
 *
 *   *"Preserve access to all currently working screens through the new navigation, using their
 *   existing presentation until redesigned. Do not replace functioning Customers, Security & Audit
 *   or other existing screens with 'not implemented'. Use that state only for genuinely missing
 *   capabilities."*
 *
 * So a destination is one of two things, and the table says which:
 *
 *   `href`      the screen EXISTS and works. Navigation goes to it, and it renders exactly as it
 *               does today until it is redesigned. Nothing is replaced.
 *   `planned`   the capability does not exist in the product at all. The destination is shown and
 *               says `טרם מומש`, because hiding it would make the approved design look smaller
 *               than it is and faking it would make the product look larger.
 *
 * ── THE CAPABILITY CODES ARE THE SERVER'S, NOT A PLAUSIBLE GUESS ────────────────────────────────
 *
 * **This is a correction, and it was a real defect.** The first version of this table named
 * `platform.subscription.read`, `platform.catalogue.read`, `platform.health.read`,
 * `platform.price_book.read`, `tenant.member.read` and `connector.read`. Every one of those reads
 * like a capability and NONE of them exists: `packages/contracts/src/capabilities.ts` declares a
 * closed set of 36 codes, and the boot validator reconciles it against `core.capability`.
 *
 * `visibleDestinations` filters on `capabilities.includes(code)`, so against the real server every
 * one of those six entries would have been filtered OUT -- the platform sidebar would have lost
 * Subscriptions, Catalogue, Price books and System status, which is precisely the "do not remove
 * access to working screens" failure, arrived at by invention rather than by omission.
 *
 * It survived the browser preview because the preview's mock API returned the invented codes: a
 * fixture written from the same wrong belief as the code it was checking agrees with it perfectly.
 * `destinations.test.ts` now binds every code here to `ALL_CAPABILITIES`, so a code that does not
 * exist is a failing test rather than a screen that quietly disappears.
 *
 * Each code below is the one the ROUTE requires, read from `apps/api/src/routes/platform.ts`:
 *
 *   Customers        `/api/platform/tenants`                 platform.tenant.read
 *   Subscriptions    `/api/platform/tenants/{uid}/subscription`  platform.tenant.read
 *   Catalogue        `/api/platform/catalogue/plans`         platform.subscription.plan.assign
 *   Price books      `/api/platform/price-books`             platform.catalogue.sku.manage
 *   Security         `/api/platform/audit`                   platform.audit.read
 *   System status    `/api/health`                           (public — no capability)
 *
 * Note what that means for Catalogue and Price books: the server deliberately serves each READ
 * under the capability that MANAGES that domain, and says so in its own comments. The menu follows
 * the route rather than inventing a read capability the catalogue does not have.
 *
 * ── CAPABILITY, NOT SURFACE, DECIDES WHAT IS SHOWN ──────────────────────────────────────────────
 *
 * `SessionSummary.capabilities` is the server's own answer about this session, so the menu and the
 * gate cannot disagree: an entry the caller could not open is not drawn, rather than drawn and then
 * refused on click (09 §5.1, CONFLICT C-1 — the same rule the company picker already follows).
 *
 * This is also what disposes of the one thing that looked like a conflict in the design review:
 * the customer sidebar's "ניהול הפלטפורמה · הרשאה נפרדת". It is navigation gated on a capability
 * no tenant session holds, so for a tenant session it simply is not drawn. Master §2 -- *"exactly
 * one active session context"* -- needs no amendment, and the implementation already matches it.
 */

import { routes } from '../routes';
import type { TextKey } from '../i18n/catalogue';
import type { IconName } from './Icon';

export type Surface = 'PLATFORM' | 'TENANT';

/**
 * ONE MEMBER OF A NAVIGATION GROUP.
 *
 * A group is one approved destination that the product serves through more than one screen. Each
 * member carries its OWN capability, so the group opens exactly as much as the session is
 * authorised for and not one link more. Nothing about either screen's authorization changes: the
 * code here is the code that screen's ROUTE already requires, and the gate decides again on every
 * request regardless of what this menu drew.
 */
export interface NavMember {
  readonly id: string;
  readonly label: TextKey;
  readonly href?: string;
  readonly capability?: string;
  readonly planned?: true;
  readonly plannedScope?: TextKey;
}

export interface Destination {
  readonly id: string;
  /** The catalogue key, so the menu is bilingual like everything else. */
  readonly label: TextKey;
  /** The kit marks every entry with an icon; these are the kit's own icon names. */
  readonly icon: IconName;
  readonly surface: Surface;
  /** Present ⇒ the screen exists and works today. Absent ⇒ `planned` or `members` must be set. */
  readonly href?: string;
  /** The server capability its route requires. Absent ⇒ shown to any session on that surface. */
  readonly capability?: string;
  /** True ⇒ the capability does not exist in the product. Renders the `not implemented` state. */
  readonly planned?: true;
  /**
   * For a `planned` entry only: what is missing, in one short phrase, when "not implemented" on
   * its own would be read as a bigger absence than the real one. See `connections` below.
   */
  readonly plannedScope?: TextKey;
  /**
   * A GROUP. The approved design names one destination; the product serves it through these
   * screens. The group header is not itself a link — it opens nothing, so it must not look like it
   * does — and each member is gated on its own capability.
   */
  readonly members?: readonly NavMember[];
  /**
   * A group that starts CLOSED.
   *
   * Both kits draw their long groups as disclosures — the customer rail's `מודולים פיננסיים` has
   * the ▼ and `הארגון שלי` and `חיבורים ותמיכה` have the ◀. The first implementation expanded all
   * of them, which put twenty-odd rows in a 190px rail and broke half the labels across three
   * lines each. Owner, having looked at it: *"התפריט הצדדי ארוך וצפוף, וכמה שמות נשברים לשורות
   * רבות ... אפשר לשמור הכול גלוי ונגיש דרך קבוצה נפתחת."*
   *
   * Closed is not hidden. The group is drawn, it names itself, it says how many places are inside
   * it, and one click opens it — which is what "discoverable" means and what an always-open list
   * stops being once it is too long to read.
   */
  readonly collapsed?: true;
}

/**
 * THE PLATFORM CONSOLE. The kit's nine destinations, in the kit's order.
 *
 * Seven have screens and keep them. Two are the ones the product cannot serve, and they are not
 * the same KIND of gap:
 *
 *   `billing`      Master §6 specifies Cardcom in full — charge batches, retries, grace,
 *                  suspension, self-service card. No payment route exists anywhere. Nothing
 *                  of this capability is built.
 *
 *   `connections`  **A PLATFORM-WIDE VIEW is missing. TENANT CONNECTIONS WORK.** Master §10 puts
 *                  connector health on the Platform Admin Console, across the whole estate. The
 *                  product has `tenant.source.list`, `tenant.source.health`, `tenant.source.probe`
 *                  and `tenant.connector.list` — all TENANT-scoped, all working, all reachable
 *                  from the customer workspace's own Connections screen. What does not exist is an
 *                  aggregate across tenants: no route answers "every customer's connector health
 *                  in one read", and faking one by looping a platform session through each
 *                  tenant's own route would cross the tenant boundary the gate exists to hold.
 *                  So the entry says the AGGREGATE is missing, not the capability.
 */
export const PLATFORM_DESTINATIONS: readonly Destination[] = [
  { id: 'overview', label: 'nav.overview', icon: 'house', surface: 'PLATFORM', href: routes.platformConsole },
  {
    id: 'customers',
    label: 'nav.customers',
    icon: 'users',
    surface: 'PLATFORM',
    href: routes.platformTenants,
    capability: 'platform.tenant.read',
  },
  {
    id: 'subscriptions',
    label: 'nav.subscriptions',
    icon: 'layers',
    surface: 'PLATFORM',
    href: routes.platformBilling,
    capability: 'platform.tenant.read',
  },
  {
    /*
     * AUDIENCE: whoever may already read billing history. `platform.billing.history.read` is the
     * declared read authority over this area; the Cardcom lifecycle capability Master s6 will need
     * does not exist yet, and inventing it here is the defect this file corrected once already.
     */
    id: 'billing',
    label: 'nav.billing',
    icon: 'credit-card',
    surface: 'PLATFORM',
    /*
     * ── IT STOPPED BEING PLANNED ON 2026-11-02 ────────────────────────────────────────────────
     *
     * The Owner: *"זה צריך להיות בנפרד ואל תכניס את הבילינג לתוך מנויים ומכסות, זה בנפרד."*
     * This destination was declared, carried the right capability, and had no screen — so the
     * charges were built twice in the wrong place before anybody looked at the navigation that
     * already named the right one.
     *
     * ── AND IT BECAME A GROUP ON 2026-11-03, WHICH IS WHERE IT WAS ASKED FOR ──────────────────
     *
     * The Owner: *"הפוך את „מרכז חיוב" בסרגל הימני לקבוצה נפתחת עם כניסה ישירה ל: סקירת חיובים,
     * ביצוע חיובים, גבייה וחובות, בקשות שינוי. הצג כל פריט בהתאם להרשאות, סמן את המסך הפעיל ופתח
     * את הקבוצה כשנמצאים באחד ממסכיה. השאר גם את הקישורים מתוך מרכז החיוב."*
     *
     * **THE FIRST ANSWER WENT TO THE WRONG FILE.** An `Area` was declared in `areas.ts`, which is
     * the TAB STRIP above a screen. It is a real part of the answer -- it is what marks the
     * current screen across the four and what keeps them together once you are inside one -- but
     * it is not the RAIL, and the rail is what he pointed at. The rail is this file, the group
     * mechanism is `members`, and `ShellNav` already does every behaviour he named: it gates each
     * member on its own capability, it puts `aria-current` on the one you are looking at, and the
     * `details` element opens when the address is inside it. Nothing had to be built. The entry
     * was a single `href`, so four screens hung off one link and three of them were unreachable
     * from the navigation.
     *
     * ── THE CAPABILITIES ARE NOT THE SAME, AND THAT IS THE POINT OF GATING EACH MEMBER ────────
     *
     * Three READ under `platform.billing.history.read` -- 08 §12B.3 row 10, held by the owner, the
     * operator and the auditor. Running the billing is `platform.billing.run.execute`, a DIFFERENT
     * authority held by fewer people, so an auditor sees three entries and not a fourth that would
     * refuse them. The server decides again on every request regardless of what was drawn here.
     */
    members: [
      {
        id: 'billingOverview',
        label: 'nav.billingOverview',
        href: routes.platformCharges,
        capability: 'platform.billing.history.read',
      },
      {
        /*
         * THE APPROVAL SCREEN, AND AFTER 2026-11-03 THAT IS ALL IT IS. The Owner: *"בתחילת החודש
         * אני נכנס למנהל הפלטפורמה ורואה רשימת חיובים מומלצים... אני בוחר את הלקוחות ומאשר את
         * החיוב במפורש."* The scheduled run prepares the list; this is where a person approves it.
         */
        id: 'billingRun',
        label: 'nav.billingRun',
        href: routes.platformBillingRun,
        capability: 'platform.billing.run.execute',
      },
      {
        id: 'billingDunning',
        label: 'nav.billingDunning',
        href: routes.platformDunning,
        capability: 'platform.billing.history.read',
      },
      {
        /*
         * 0055. THE SUPPORT QUEUE, under the READING capability and not the acting one: the
         * operator and the auditor need to see what customers are complaining about, and only the
         * support manager and the owner may move a ticket. The screen withholds the moves.
         */
        id: 'supportQueue',
        label: 'nav.supportQueue',
        href: routes.platformSupportQueue,
        capability: 'platform.support.ticket.read',
      },
      {
        id: 'billingChangeRequests',
        label: 'nav.billingChangeRequests',
        href: routes.platformChangeRequests,
        capability: 'platform.billing.history.read',
      },
    ],
  },
  {
    /*
     * ── THE APPROVED GROUP: קטלוג ומחירונים ────────────────────────────────────────────────────
     *
     * The kit names ONE destination here — the catalogue AND the price books — and the Owner kept
     * it: *"Keep the approved קטלוג ומחירונים navigation group, with separate permission-gated
     * links to Catalogue and Price Books. Do not relax either screen's authorization."*
     *
     * So it is a group of two, and each member carries the capability its own route requires:
     *
     *   Catalogue    `/api/platform/catalogue/plans`   platform.subscription.plan.assign
     *   Price books  `/api/platform/price-books`       platform.catalogue.sku.manage
     *
     * NOTHING IS RELAXED. The earlier tenth-entry workaround existed because the catalogue SCREEN
     * cannot carry a link to the price books: `platformCatalogue.dom.test.tsx` holds D-X3-1 — the
     * catalogue says what a plan INCLUDES and never what it COSTS — by refusing the string `Price`
     * anywhere in that screen's source. That guard is untouched, and so is every server-side check
     * on both routes. The second link lives in the MENU, which is not either screen.
     *
     * A session holding only one of the two capabilities sees only that one link. A session holding
     * neither does not see the group at all — which is a PERMISSION decision, not an availability
     * one, and is the only reason anything here is ever hidden.
     */
    id: 'catalogue',
    label: 'nav.catalogue',
    icon: 'package',
    surface: 'PLATFORM',
    members: [
      {
        id: 'cataloguePlans',
        label: 'nav.cataloguePlans',
        href: routes.platformCatalogue,
        capability: 'platform.subscription.plan.assign',
      },
      {
        id: 'priceBooks',
        label: 'nav.priceBooks',
        href: routes.platformPriceBooks,
        capability: 'platform.catalogue.sku.manage',
      },
    ],
  },
  {
    /*
     * AUDIENCE: whoever may already see the estate. Master s10 puts connector health on this
     * console beside tenants, trials and churn, and `platform.tenant.read` is the declared
     * authority over exactly that view.
     */
    id: 'connections',
    label: 'nav.connections',
    icon: 'plug',
    surface: 'PLATFORM',
    capability: 'platform.tenant.read',
    planned: true,
    plannedScope: 'planned.connectionsPlatformWide',
  },
  {
    id: 'security',
    label: 'nav.security',
    icon: 'shield',
    surface: 'PLATFORM',
    href: routes.platformSecurity,
    capability: 'platform.audit.read',
  },
  {
    /* `/api/health` is declared in `public.ts` and requires no capability, so neither does this. */
    id: 'health',
    label: 'nav.health',
    icon: 'activity',
    surface: 'PLATFORM',
    href: routes.platformHealth,
  },
  {
    /*
     * THE KIT'S NINTH ENTRY IS `settings`, and its own settings screen lists "מדיניות מסחרית"
     * among four panels. Commercial policy is the one of those four the product actually serves,
     * so this entry opens it rather than a settings index that would be three-quarters empty.
     * `/api/platform/commercial-policy` requires `platform.subscription.edit`.
     */
    id: 'settings',
    label: 'nav.settings',
    icon: 'settings',
    surface: 'PLATFORM',
    href: routes.platformCommercialPolicy,
    capability: 'platform.subscription.edit',
  },
];

/**
 * THE ELEVEN FINANCIAL MODULES, in the approved kit's own order and its own words.
 *
 * Not one of them has a route, a capability code or a table. `planned` is therefore the whole truth
 * about each, and `planned.module` says it in the menu. They share one audience — a person who may
 * read a company in this office — because an accounting module is office work.
 */
export const FINANCIAL_MODULES: readonly NavMember[] = (
  [
    ['financialStatements', 'nav.mod.financialStatements'],
    ['cfoQueues', 'nav.mod.cfoQueues'],
    ['vatControl', 'nav.mod.vatControl'],
    ['creditCards', 'nav.mod.creditCards'],
    ['bankReconciliation', 'nav.mod.bankReconciliation'],
    ['bankStatements', 'nav.mod.bankStatements'],
    ['form126and856', 'nav.mod.form126and856'],
    ['collections', 'nav.mod.collections'],
    ['qualityControl', 'nav.mod.qualityControl'],
    ['controls', 'nav.mod.controls'],
    ['accountantLetters', 'nav.mod.accountantLetters'],
  ] as const
).map(([id, label]) => ({
  id,
  label,
  capability: 'companies.company.read',
  planned: true as const,
  plannedScope: 'planned.module' as const,
}));

/**
 * The same eleven, for the home screen library. One declaration: the rail and the library are two
 * views of one list, and a second copy would let them drift apart module by module.
 */
export const FINANCIAL_MODULES_FOR_HOME = FINANCIAL_MODULES;

/**
 * ================================================================================================
 *  THE CUSTOMER WORKSPACE, FROM THE APPROVED CUSTOMER KIT.
 * ================================================================================================
 *
 * `GLPOINT-CUSTOMER-DESIGN-2026-10-15.zip`, Owner-approved 2026-10-15. Its rail, in its order:
 *
 *     הבית שלי · החברות שלי · משימות · מסמכים · דוחות
 *     ▼ מודולים פיננסיים  — eleven accounting modules
 *     ◀ הארגון שלי
 *     ◀ חיבורים ותמיכה
 *     ◀ ניהול הפלטפורמה · הרשאה נפרדת
 *
 * Every entry is here. Five of them open screens that exist today; the eleven financial modules and
 * three of the first five do not exist at all, and they are SHOWN to the audience that will own
 * them, each carrying its real state, because that is what the Owner asked for twice:
 *
 *   *"Preserve every planned capability in the approved navigation and dashboard, subject to
 *   permissions. Do not hide it merely because it is unavailable."*
 *   *"planned availability must not bypass navigation permissions ... Separate permission to see a
 *   capability from whether it is implemented or connected."*
 *
 * So each unbuilt entry names the capability of its nearest existing authority, which for office
 * work is `companies.company.read` — a person who may not see a single company in the office is not
 * the audience for its accounting modules, built or unbuilt.
 *
 * ── THE PLATFORM ENTRY, AND WHY IT IS NOT A CONFLICT ────────────────────────────────────────────
 *
 * The kit's last rail entry is *"ניהול הפלטפורמה · הרשאה נפרדת"*. It is navigation gated on a
 * capability no tenant session holds, so for a tenant session it is simply not drawn — which is the
 * ordinary working of the rule above and needs no special case. Master §2 — *"one person may have
 * one identity but exactly one active session context"* — is matched by the implementation exactly,
 * and the design does not grant a customer platform access by listing the door.
 */
export const TENANT_DESTINATIONS: readonly Destination[] = [
  { id: 'home', label: 'nav.home', icon: 'house', surface: 'TENANT', href: routes.tenantHome },
  {
    id: 'companies',
    label: 'nav.companies',
    icon: 'building',
    surface: 'TENANT',
    href: routes.companies,
    capability: 'companies.company.read',
  },
  {
    /*
     * ── NO LONGER `planned`. **§6, 2026-10-29.** ───────────────────────────────────
     *
     * It carried `planned: true` and `companies.company.read` -- the capability of its nearest
     * existing authority, which is what an UNBUILT entry does. The module exists now, so the entry
     * names its OWN capability: `tasks.item.read`.
     *
     * THAT CHANGES WHO SEES THE MENU ITEM, and deliberately. An office whose staff hold
     * `companies.company.read` and not `tasks.item.read` no longer sees a destination they cannot
     * open -- and since 0027 grants the codes to ONE role per existing office, that is most people
     * until their owner delegates. A menu entry that opened a refusal would be worse than its
     * absence: *"אין להסתיר יכולת משום שהיא אינה זמינה"* is about availability, not about authority.
     */
    id: 'tasks',
    label: 'nav.tasks',
    icon: 'layers',
    surface: 'TENANT',
    href: routes.tenantWorkItems,
    capability: 'tasks.item.read',
  },
  {
    id: 'documents',
    label: 'nav.documents',
    icon: 'package',
    surface: 'TENANT',
    capability: 'companies.company.read',
    planned: true,
    plannedScope: 'planned.module',
  },
  {
    id: 'reports',
    label: 'nav.reports',
    icon: 'chart-column',
    surface: 'TENANT',
    capability: 'companies.company.read',
    planned: true,
    plannedScope: 'planned.module',
  },
  {
    /*
     * THE ELEVEN, AS THE KIT NAMES THEM. Master §22 requires VAT control, collections, bank
     * statements and credit cards for Commercial V1, so these are specified-but-unbuilt and not
     * invention. None has a route, a capability or a table. They are listed because hiding them
     * would misrepresent the approved design, and each says `טרם מומש` because listing them
     * without that would misrepresent the product.
     */
    id: 'financialModules',
    collapsed: true,
    label: 'nav.financialModules',
    icon: 'chart-column',
    surface: 'TENANT',
    members: FINANCIAL_MODULES,
  },
  /*
   * ── ONE ENTRY, NOT A GROUP. **2026-10-28.** ───────────────────────────────────────────────────
   *
   * Owner, with his reference beside a screenshot of ours: *"לא צריך שיהיה בבר הגדרות ומתחת פרטי
   * הארגון ומתחת משתמשים ואז בטאבים יהיה עוד פעם רשום את זה, זה כפילות מיותרת. מספיק שבבר יהיה
   * רשום הגדרות ושלוחצים, אזי במסך יש את ההגדרות."*
   *
   * This group expanded into four destinations in the rail; the tab bar above the content listed
   * the same screens again; and the settings screen's own strip listed them a third time. The rail
   * now carries the word and nothing under it, and the screen behind it is the grid of categories
   * his reference shows.
   *
   * NOTHING BECOMES UNREACHABLE AND NOTHING IS UNGATED. Each destination that was listed here is a
   * card on that grid, and each card opens a screen that carries its own capability — the gate
   * decides, as it did before, and a card is a link. What is gone is the second and third copy of
   * the same list.
   */
  {
    id: 'office',
    label: 'nav.office',
    icon: 'settings',
    surface: 'TENANT',
    href: routes.tenantAdmin,
    capability: 'tenant.settings.read',
  },
  /*
   * ── AND THIS GROUP IS GONE FROM THE RAIL. **2026-10-28, the same instruction.** ───────────────
   *
   * The Owner's own list of settings categories is *"החברה שלי, הנהח (לפי העניין), חיבורים
   * למערכות הנהח, חיבור ל-OPEN FINANCE AI"* — so a connection to an accounting system IS a
   * settings category in the shape he is asking for, and it is a card on that grid.
   *
   * Keeping this group as well would put one screen in two places in the same rail, which is the
   * duplication he is pointing at wearing a different hat. Support access shares the security
   * screen's address and is reached from it, where the grants it shows actually live.
   *
   * The connections screen keeps its OWN bar one level down (`CONNECTIONS_AND_SUPPORT` in
   * `areas.ts`), exactly as every other category does.
   */
  {
    /*
     * THE KIT'S LAST ENTRY. Gated on a PLATFORM capability, which no tenant session holds, so a
     * customer never sees it. It is declared rather than deleted because the design declares it,
     * and because deleting it would hide the fact that the gate is what keeps it away.
     */
    id: 'platformAdministration',
    label: 'nav.platformAdministration',
    icon: 'shield',
    surface: 'TENANT',
    href: routes.platformConsole,
    capability: 'platform.tenant.read',
  },
];

/**
 * ================================================================================================
 *  TWO AXES, AND THEY ARE NOT THE SAME QUESTION.
 * ================================================================================================
 *
 * Owner correction, 2026-10-18, verbatim: *"planned availability must not bypass navigation
 * permissions. Keep planned capabilities visible to the appropriate audience, with their actual
 * state shown. Separate permission to see a capability from whether it is implemented or
 * connected."*
 *
 * The first version of this function had `planned === true` short-circuit the capability check, so
 * an unbuilt capability was shown to EVERYONE — including a session whose audience it will never
 * be. That is a privilege leak made of nothing but a menu: it tells a reader what the product is
 * going to do and who it is going to do it for, on a surface they were not given.
 *
 * So the two axes are now separate everywhere:
 *
 *   `capability`  WHO MAY SEE IT. The audience. Asked identically of a built entry and an unbuilt
 *                 one — being unfinished is not a reason to widen an audience.
 *   `planned`     WHAT STATE IT IS IN. Not yet implemented, configuration required, failed to
 *                 load, no data yet. It decides what the row SAYS, never who may read the row.
 *
 * The earlier instruction is unchanged and still holds: a planned capability is never hidden FOR
 * BEING UNAVAILABLE. It is hidden only when the session is not its audience, which is the same
 * reason a built one is hidden, and it is shown to its audience with its real state on it.
 *
 * ── WHICH CAPABILITY GATES SOMETHING THAT DOES NOT EXIST ────────────────────────────────────────
 *
 * The code a planned entry will eventually require has not been declared — the catalogue is a
 * closed set of 36 and inventing a thirty-seventh here is exactly the defect this file already
 * corrected once. So each planned entry names the capability of its NEAREST EXISTING AUTHORITY:
 * the one a person must already hold to be the audience for that area. It is the honest available
 * answer, it is a real declared code, and when the capability is finally declared this is the line
 * that changes.
 */
function permitted(
  entry: { readonly capability?: string },
  capabilities: readonly string[],
): boolean {
  return entry.capability === undefined || capabilities.includes(entry.capability);
}

/** The members of a group this session may see, in approved order. */
export function visibleMembers(
  destination: Destination,
  capabilities: readonly string[],
): readonly NavMember[] {
  return (destination.members ?? []).filter((m) => permitted(m, capabilities));
}

/**
 * What this session may see, in approved order.
 *
 * A GROUP is shown when at least one of its members is. A group whose every member is denied would
 * otherwise be a heading that opens nothing, which is the "unexplained disabled control" the Owner
 * ruled out — so it is absent, and absent for a permission reason rather than an availability one.
 */
export function visibleDestinations(
  surface: Surface,
  capabilities: readonly string[],
): readonly Destination[] {
  const all = surface === 'PLATFORM' ? PLATFORM_DESTINATIONS : TENANT_DESTINATIONS;
  return all.filter((d) =>
    d.members === undefined
      ? permitted(d, capabilities)
      : visibleMembers(d, capabilities).length > 0,
  );
}
